import { Http } from './http';
import {
  CreditTransaction,
  Order,
  OrderTransaction,
  OrderTransactionPartner,
} from '@types';

class OrdersHttp extends Http {
  prefix = 'order';

  async exportPositions(queryParams: any): Promise<ExportPosition[]> {
    const res = await this.get<ExportPosition[]>('/export-positions', {
      params: queryParams,
    });

    return res.data;
  }

  async exportPositionsByCredit(queryParams: any): Promise<ExportPositionByCredit[]> {
    const res = await this.get<ExportPositionByCredit[]>('/export-positions-by-credit', {
      params: queryParams,
    });

    return res.data;
  }

  async getOrdersByUser(userId: number): Promise<Order[]> {
    const res = await this.get<Order[]>(`?userId=${userId}`);

    return res.data;
  }

  async payOperation(params: PayOperationParams): Promise<OrderTransaction> {
    const { orderId, partner, amount } = params;
    const url = `/${orderId}/pay-operation`;
    const res = await this.post<CreditTransaction>(url, { partner, amount });

    return res.data;
  }

  async cancelOrder(orderId: number): Promise<Order> {
    const url = `/${orderId}/cancel`;
    const res = await this.delete<Order>(url);

    return res.data;
  }

  async rollbackOrder(orderId: number): Promise<Order> {
    const url = `/${orderId}/rollback`;
    const res = await this.delete<Order>(url);

    return res.data;
  }

  async removeOrders(orderIds: number[]): Promise<void> {
    const url = '/remove';
    await this.post<RemoveOrdersByIdsParams>(url, { orderIds });

    return;
  }

  async getOrder(orderId: number): Promise<Order> {
    const res = await this.get<Order>(`/${orderId}`);

    return res.data;
  }

  async saveOrder(params: SaveOrderParams): Promise<Order> {
    const res = await this.post<Order>('', params);

    return res.data;
  }

  async updateOrder(params: UpdateOrderParams): Promise<void> {
    const url = `/${params.orderId}`;
    await this.put(url, params);

    return;
  }

  async downloadQuotationFile(orderId: number): Promise<void> {
    const filename = 'Quotation Document.pdf';

    await this.downloadPdf(`/${orderId}/download-quotation-file`, filename);
  }

  async updateBolFile(orderId: number, fileId: number): Promise<void> {
    await this.patch(`/${orderId}/update-bol-file`, {
      fileId,
    });
  }

  async downloadBolFile(orderId: number): Promise<void> {
    const filename = 'BOL.pdf';

    await this.downloadPdf(`/${orderId}/download-bol-file`, filename);
  }
}

export const ordersHttp = new OrdersHttp();

interface PayOperationParams {
  orderId: number;
  partner: OrderTransactionPartner;
  amount: string;
}

interface RemoveOrdersByIdsParams {
  orderIds: number[];
}

export interface ExportPosition {
  product: string;
  brand: string | null;
  category: string | null;
  subcategory: string | null;
  purchasedQuantity: bigint;
  totalExpend: bigint;
  averageUnitPrice: bigint;
}

export interface ExportPositionByCredit {
  credit: string;
  accredited: string;
  buyer: string | null;
  product: string;
  brand: string | null;
  category: string | null;
  subcategory: string | null;
  purchasedQuantity: bigint;
  totalExpend: bigint;
  averageUnitPrice: bigint;
}

export interface SaveOrderParams {
  id?: number;
  composition: any[];
  userId: number;
  creditId: number | null;
  discountAmount: number;
  isrPercent: number | null;
  iepsPercent: number | null;
}

export interface UpdateOrderParams {
  orderId: number;
  creditId: number | null;
  composition: any[];
  userId: number;
  requestHours?: number;
  discountAmount?: number | null;
  isrPercent?: number | null;
  iepsPercent?: number | null;
  retailerName?: string | null;
  zipState?: string | null;
  sendQuotation?: boolean;
  updateQuotation?: boolean;
  confirmOrder?: boolean;
  cancelOrder?: boolean;
  collectionCode?: string | null;
  resetPayment?: boolean;
}
